import React from "react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import Header from "./Header";

function Report() {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  const [dateValue, setDateValue] = React.useState("");
  const [isValidDate, setIsValidDate] = React.useState(true);
  const [serverError, setServerError] = React.useState(""); // <-- Error message

  const formatDateForAPI = (dateString) => {
    if (!dateString || dateString.length !== 10) return null;
    const [day, month, year] = dateString.split("/");
    if (!day || !month || !year) return null;
    return `${year}-${month}-${day}`;
  };

  const parentsReportMutation = useMutation({
    mutationKey: ["getReport"],
    mutationFn: (data) => axiosInstance.post("api/parentsreport", data),
  });

  function handleSubmit() {
    setServerError(""); // Clear error on submit
    parentsReportMutation.mutate(
      {
        dateofbirth: formatDateForAPI(dateValue),
        yearstandardId: "1",
      },
      {
        onSuccess: (data) => {
          if (
            data?.data?.status_code === "422" &&
            data?.data?.status === "Fail"
          ) {
            setServerError(data?.data?.message || "Invalid date of birth!");
            setDateValue(""); // <-- Clear input on error
            return;
          }
          const report = data.data.report;
          navigate("/report/details", { state: { report } });
        },
        onError: (e) => {
          const serverMessage = e?.response?.data?.message;
          const serverStatus = e?.response?.data?.status_code;
          if (serverStatus === "422") {
            setServerError(serverMessage || "Invalid date of birth!");
            setDateValue(""); // <-- Clear input on error
          } else {
            setServerError(serverMessage || "An error occurred. Please try again.");
            setDateValue(""); // <-- Clear input on error
          }
        },
      }
    );
  }

  const isValidDateFormat = (day, month, year) => {
    const date = new Date(year, month - 1, day);
    return (
      date.getDate() === Number(day) &&
      date.getMonth() === Number(month) - 1 &&
      date.getFullYear() === Number(year)
    );
  };

  const handleDateChange = (e) => {
    let input = e.target.value.replace(/\D/g, "");

    if (e.nativeEvent.inputType === "deleteContentBackward") {
      input = input.slice(0, -1);
    }

    if (input.length <= 8) {
      let formatted = input;
      if (input.length >= 2) formatted = formatted.replace(/^(\d{2})/, "$1/");
      if (input.length >= 4)
        formatted = formatted.replace(/^(\d{2})\/(\d{2})/, "$1/$2/");

      if (input.length === 8) {
        const [day, month, year] = formatted.split("/");
        const isValid = isValidDateFormat(day, month, year);
        setIsValidDate(isValid);
      } else {
        setIsValidDate(true);
      }

      setDateValue(formatted);
      setServerError(""); // Clear server error if user edits date
    }
  };

  // Handle Enter key press to submit the form
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default form submission behavior
      
      // Only submit if we have a valid date and it's complete (10 characters)
      if (dateValue.length === 10 && isValidDate && !parentsReportMutation.isPending) {
        handleSubmit();
      }
    }
  };

  // Check if button should be disabled - no DOB entered or invalid date
  const isButtonDisabled = dateValue.length !== 10 || !isValidDate || parentsReportMutation.isPending;

  return (
    <>
      <Header />
      <div
        className="min-h-screen flex flex-col  items-center justify-center  font-mali"
        style={{
          backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="bg-[#fc8f79] py-12 rounded-2xl text-center flex gap-10 flex-col justify-evenly self-center items-center mx-10 px-12">
          <div>
            <p className="text-5xl  text font-bold text-center">Hi There!</p>
            <p className="text-center font-medium text-xl mt-4">
              Please ask a grown up to enter their date of birth to continue
            </p>
          </div>

          <div className="w-[400px] mx-auto">
            <p className="text-xl font-bold text-left">Date of Birth</p>
            <div className="relative my-4 bg-slate-300">
              <input
                type="text"
                value={dateValue}
                onChange={handleDateChange}
                onKeyPress={handleKeyPress} // Add Enter key functionality
                placeholder="DD/MM/YYYY"
                className={`p-2 border rounded-md w-full focus:outline-none focus:ring-2 ${
                  isValidDate
                    ? "focus:ring-primary-brown border-gray-300"
                    : "focus:ring-red-500 border-red-500"
                }`}
                maxLength="10"
              />
            </div>
            {/* Validation error */}
            {!isValidDate && (
              <p className="text-red-500 text-sm mt-1">
                Please enter a valid date
              </p>
            )}
            {/* Server error */}
            {serverError && (
              <p className="text-red-500 text-sm mt-1">{serverError}</p>
            )}
            <button
              onClick={handleSubmit}
              disabled={isButtonDisabled} // Disable button based on conditions
              className={`w-full py-3 rounded-full transition-colors text-xl font-semibold flex items-center justify-center ${
                isButtonDisabled
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed" // Disabled styling
                  : "bg-primary-dark-blue text-white hover:bg-blue-900 cursor-pointer" // Enabled styling
              }`}
            >
              {parentsReportMutation.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                "Continue"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default Report;
